// =============================================================================
// useLostLeadsAnalytics — the Lost Leads analytics suite + dashboard revived card
// =============================================================================
// ONE fetch of get_lost_leads_analytics (sql/analytics/get_lost_leads_analytics.sql).
// Every number on the Lost Leads "Analytics" tab AND the dashboard "Revived Lost
// Leads" card comes from this single SQL definition, so the two can never drift
// (they did: the dashboard used a 30-day client-side slice while the Booked tab
// used the /lostlead rule — $168,350 vs $159,851 all-time on 2026-10-01).
//
// Range = CLAIM date in org tz (Mako ruling 2026-10-01). from/to null = all time.
// Cache: stale-while-revalidate in localStorage (docs/REF_caching_localstorage_versioning_ttl.md).
//   range ends before today -> 24 h TTL (history is settled)
//   range includes today    -> 5 min TTL (claims + dials move all day)
// The key carries CACHE_VERSION, so a /cachebump evicts it.
// Access: exec / manager (server returns 42501 to everyone else).
// =============================================================================

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { CACHE_VERSION } from "../../lib/cacheVersion";

/** One group's counts. Rates are derived on the client with pctOrDash so every
 *  surface divides the same numbers. */
export interface LostLeadsGroupMetrics {
  claims: number;
  leads: number;
  /** Claims whose tier is rebuilt from history (no stamp at claim time). */
  reconstructed: number;
  /** Claimer's dial attempts (double dial within 5 min = 1). */
  attempts: number;
  dials: number;
  /** Outbound calls connected 45s+ (Mako 2026-08-09). */
  connects: number;
  dialed_claims: number;
  claims_7plus: number;
  connected_claims: number;
  /** Claims with a call booked inside the claim's own window. */
  booked_claims: number;
  shows: number;
  no_shows: number;
  /** R14 orphan pre-calls inside the claim's window (no outcome ever filed). */
  orphan_precalls: number;
  /** shows minus rescheduled (R21/R24) — the close-rate denominator. */
  decisive: number;
  /** PIF + CPP. */
  wins: number;
  pending: number;
  cash: number;
  revivals: number;
  /** Cash of sales CREDITED to the claim (rule C, get_claim_revivals). */
  revival_cash: number;
  /** PIF + CPP sales credited to the claim (rule C). */
  revival_wins: number;
  /** Credited funnel (Mako 2026-10-01: "close rate and cash collected that should all
   *  follow the credit"). Same credited sales as revival_wins / revival_cash, so
   *  Final close = revival_wins ÷ revival_decisive. Use creditedCloseRates(). */
  revival_shows: number;
  revival_no_shows: number;
  revival_decisive: number;
  revival_pending: number;
  ended: number;
  wasted: number;
  active: number;
  selfset_rows: number;
  selfset_wins: number;
  selfset_cash: number;
}

export interface LostLeadsTierRow extends LostLeadsGroupMetrics {
  tier: number | null;
}
export interface LostLeadsLeadTypeRow extends LostLeadsGroupMetrics {
  lead_type: string | null;
}
export interface LostLeadsRepRow extends LostLeadsGroupMetrics {
  closer_id: string;
  name: string | null;
  role: string | null;
  member_status: string | null;
}

export interface RevivedDeal {
  claim_id: string;
  lead_id: string;
  lead_email: string | null;
  lead_name: string | null;
  tier: number | null;
  tier_source: "stamped" | "reconstructed";
  lead_type: string | null;
  claimed_by: string;
  claimed_by_name: string | null;
  claimed_by_role: string | null;
  claimed_at: string;
  first_booking_at: string | null;
  attempts: number;
  connects: number;
  last_outcome: string | null;
  closed_by: string | null;
  closed_by_name: string | null;
  wins: number;
  cash: number;
  self_set: boolean;
}

export interface LostLeadsAnalytics {
  meta: {
    generated_at: string;
    timezone: string;
    from: string | null;
    to: string | null;
    min_attempts: number;
  };
  totals: LostLeadsGroupMetrics | null;
  revived_totals: LostLeadsGroupMetrics | null;
  by_tier: LostLeadsTierRow[];
  by_lead_type: LostLeadsLeadTypeRow[];
  by_rep: LostLeadsRepRow[];
  release_reasons: { reason: string; claims: number }[];
  revived_deals: RevivedDeal[];
}

const LIVE_TTL_MS = 5 * 60 * 1000;
const HISTORICAL_TTL_MS = 24 * 60 * 60 * 1000;

// PostgREST serialises Postgres numeric as a STRING (/debug #87). SUM() over numeric
// (cash) arrives as "20000.00"; coerce every numeric field once, here.
const NUMERIC_KEYS: (keyof LostLeadsGroupMetrics)[] = [
  "claims",
  "leads",
  "reconstructed",
  "attempts",
  "dials",
  "connects",
  "dialed_claims",
  "claims_7plus",
  "connected_claims",
  "booked_claims",
  "shows",
  "no_shows",
  "orphan_precalls",
  "decisive",
  "wins",
  "pending",
  "cash",
  "revivals",
  "revival_cash",
  "revival_wins",
  "revival_shows",
  "revival_no_shows",
  "revival_decisive",
  "revival_pending",
  "ended",
  "wasted",
  "active",
  "selfset_rows",
  "selfset_wins",
  "selfset_cash",
];
function normGroup<T extends object>(g: T | null): T | null {
  if (!g) return null;
  const out = { ...g } as Record<string, unknown>;
  for (const k of NUMERIC_KEYS) out[k] = Number(out[k] ?? 0) || 0;
  return out as T;
}
function normalize(raw: LostLeadsAnalytics): LostLeadsAnalytics {
  return {
    ...raw,
    totals: normGroup(raw.totals),
    revived_totals: normGroup(raw.revived_totals),
    by_tier: (raw.by_tier ?? []).map((r) => normGroup(r)!),
    by_lead_type: (raw.by_lead_type ?? []).map((r) => normGroup(r)!),
    by_rep: (raw.by_rep ?? []).map((r) => normGroup(r)!),
    release_reasons: (raw.release_reasons ?? []).map((r) => ({
      ...r,
      claims: Number(r.claims) || 0,
    })),
    revived_deals: (raw.revived_deals ?? []).map((d) => ({
      ...d,
      attempts: Number(d.attempts) || 0,
      connects: Number(d.connects) || 0,
      wins: Number(d.wins) || 0,
      cash: Number(d.cash) || 0,
    })),
  };
}

function cacheKey(orgId: string, from: string | null, to: string | null) {
  return `altara_${CACHE_VERSION}_lost_leads_analytics_${orgId}_${from ?? "all"}_${to ?? "all"}`;
}

interface CacheEntry {
  data: LostLeadsAnalytics;
  at: number;
}
function readCache(key: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as CacheEntry) : null;
  } catch {
    return null;
  }
}
function writeCache(key: string, data: LostLeadsAnalytics) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, at: Date.now() }));
  } catch {
    // quota / private mode: the fetch result still renders, just uncached
  }
}

// In-flight dedupe (P3-onefetch, 2026-10-01): one RPC per cache key at a time.
// StrictMode runs this effect twice in dev, and the Analytics tab + dashboard Revived
// card can mount on the same key; without this each mount paid a full server run
// (cold tab measured 2 calls). The promise is shared, so whoever mounts second just
// awaits the first request. Entry is dropped when it settles.
type AnalyticsResult = { data: LostLeadsAnalytics | null; error: string | null };
const inflight = new Map<string, Promise<AnalyticsResult>>();
function fetchAnalytics(
  key: string,
  orgId: string,
  from: string | null,
  to: string | null
): Promise<AnalyticsResult> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = Promise.resolve(
    supabase!.rpc("get_lost_leads_analytics", { p_org: orgId, p_from: from, p_to: to })
  )
    .then(({ data, error }): AnalyticsResult => {
      if (error) {
        logger.error("useLostLeadsAnalytics: fetch failed", error);
        return { data: null, error: error.message };
      }
      const norm = normalize(data as LostLeadsAnalytics);
      writeCache(key, norm);
      return { data: norm, error: null };
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/**
 * @param from   YYYY-MM-DD claim-date start in org tz, or null for all time
 * @param to     YYYY-MM-DD claim-date end (inclusive), or null for all time
 * @param todayKey  YYYY-MM-DD today in org tz — decides live vs historical TTL
 */
/** R14 show-rate denominator (Mako 2026-10-01: "we need to match r14 always"):
 *  shows + SCO no-shows + orphan pre-calls. Every Lost Leads show rate divides by this. */
export function showRateDenominator(
  g: Pick<LostLeadsGroupMetrics, "shows" | "no_shows" | "orphan_precalls">
): number {
  return g.shows + g.no_shows + g.orphan_precalls;
}

/** Close-rate counts, CREDITED (Mako 2026-10-01: "close rate and cash collected that
 *  should all follow the credit"). Numerators and denominator are the same credited
 *  sales as Closes + Cash, so a row can never read 100% close with 0 closes.
 *  Final = wins ÷ decisive (scored). Pending = (wins + pending_close) ÷ decisive (shown,
 *  never scored, R29/R30). Every Lost Leads close rate reads this. */
export function creditedCloseCounts(
  g: Pick<LostLeadsGroupMetrics, "revival_wins" | "revival_pending" | "revival_decisive">
): { wins: number; withPending: number; decisive: number } {
  return {
    wins: g.revival_wins,
    withPending: g.revival_wins + g.revival_pending,
    decisive: g.revival_decisive,
  };
}

export function useLostLeadsAnalytics(
  orgId: string | null,
  from: string | null,
  to: string | null,
  todayKey: string,
  enabled = true
) {
  const key = orgId ? cacheKey(orgId, from, to) : "";
  const [state, setState] = useState<{
    key: string;
    data: LostLeadsAnalytics | null;
    error: string | null;
    fetching: boolean;
  }>({ key: "", data: null, error: null, fetching: false });

  useEffect(() => {
    if (!enabled || !orgId || !supabase) return;
    let cancelled = false;
    const cached = readCache(key);
    const ttl = to === null || to >= todayKey ? LIVE_TTL_MS : HISTORICAL_TTL_MS;
    const fresh = !!cached && Date.now() - cached.at < ttl;
    // Stale-while-revalidate: paint the cached copy now, refetch if it is old.
    queueMicrotask(() => {
      if (cancelled) return;
      setState({ key, data: cached?.data ?? null, error: null, fetching: !fresh });
    });
    if (fresh)
      return () => {
        cancelled = true;
      };
    fetchAnalytics(key, orgId, from, to).then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        setState((s) => ({
          key,
          data: s.key === key ? s.data : null,
          error,
          fetching: false,
        }));
        return;
      }
      setState({ key, data, error: null, fetching: false });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, from, to, todayKey, key, enabled]);

  const current = state.key === key;
  return {
    data: current ? state.data : null,
    error: current ? state.error : null,
    loading: enabled && !!orgId && (!current || (state.fetching && !state.data)),
    refreshing: current && state.fetching && !!state.data,
  };
}
